import React, { useEffect, useState } from 'react';
import { useGetAccountInfo } from '@multiversx/sdk-dapp/hooks';
import { BigUIntValue, BooleanValue, BytesValue, TypedValue } from '@multiversx/sdk-core/out';
import BigNumber from 'bignumber.js/bignumber';
import clsx from 'clsx';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UpDown from 'assets/img/updown.png';

import {
  SwapTabs,
  CustomButton,
  PaymentPanel,
  TokenSelector
} from 'components';

import { contracts } from 'config/config';
import { BigMax, BigZero, coinNames } from 'helpers/constant';
import { convertEsdtToWei, convertWeiToEsdt, getSymbol, shortenEsdt } from 'helpers/utils';
import { useInteraction } from 'Interaction';
import { routeNames } from 'routes';
import styles from './styles.module.scss';

export const Swap = () => {
  const [showFirstTokenModal, setShowFirstTokenModal] = useState(false);
  const [showSecondTokenModal, setShowSecondTokenModal] = useState(false);

  const [slippage, setSlippage] = useState(0.01);

  const [isFixedInput, setIsFixedInput] = useState(false);
  const [isFixedoutput, setIsFixedOutput] = useState(false);

  const [firstTokenAmount, setFirstTokenAmount] = useState(BigZero);
  const [firstTokenAmountWithoutSlippage, setFirstTokenAmountWithoutSlippage] = useState(BigZero);
  const [secondTokenAmount, setSecondTokenAmount] = useState(BigZero);

  const [wrapRequired, setWrapRequired] = useState(false);
  const [unwrapRequired, setUnwrapRequired] = useState(false);

  const [firstToken, setFirstToken] = useState('');
  const [secondToken, setSecondToken] = useState('');

  const [validSecondTokens, setValidSecondTokens] = useState<string[]>([]);
  const [validPaths, setValidPaths] = useState<string[][]>([]);
  const [searchingRoutes, setSearchingRoutes] = useState(false);
  const [optimalPath, setOptimalPath] = useState<string[]>([]);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const { pairTokens, pairTokenInfos, swapPaths, callMethod, viewMethod } = useInteraction();
  const { account: {address: userAddress} } = useGetAccountInfo();
  const navigate = useNavigate();

  useEffect(() => {
    if(!validSecondTokens.length) return;
    if(firstToken || secondToken) return;

    const firstTokenId = searchParams.get('firstToken');
    const secondTokenId = searchParams.get('secondToken');

    if(firstTokenId && firstTokenId !== firstToken) {
      setFirstToken(firstTokenId);
    }

    if(secondTokenId && secondTokenId !== secondToken) {
      setSecondToken(secondTokenId);
    }
  }, [searchParams, validSecondTokens]);

  useEffect(() => {
    setFirstTokenAmount(BigZero);
    setSecondTokenAmount(BigZero);
    setOptimalPath([]);

    
    if(firstToken && secondToken) {
      const paths: string[][] = [];

      let token_in = firstToken;
      let token_out = secondToken;

      if(firstToken === coinNames[0]) {
        token_in = coinNames[1];
        setWrapRequired(true);
      } else {
        setWrapRequired(false);
      }
      
      if(secondToken === coinNames[0]) {
        token_out = coinNames[1];
        setUnwrapRequired(true);
      } else {
        setUnwrapRequired(false);
      }
      
      for(const path of swapPaths) {
        if(path[0] === token_in && path[path.length - 1] === token_out) {
          paths.push(path);
        }
      }

      setValidPaths(paths);

      setSearchParams({
        firstToken: firstToken,
        secondToken: secondToken
      });
    }
  }, [firstToken, secondToken]);

  useEffect(() => {
    if(!firstToken) {
      setValidSecondTokens(pairTokens);
    } else {
      const secondTokens: string[] = [];

      for(const path of swapPaths) {
        let first = firstToken;
        if(firstToken === coinNames[0]) first = coinNames[1];

        if(path[0] === first) {
          const second = path[path.length - 1];
          if(!secondTokens.includes(second)) {
            secondTokens.push(second);
          }
        }
      }

      if(secondTokens.includes(coinNames[1])) {
        secondTokens.unshift('EGLD');
      }
      
      if(!secondTokens.includes(secondToken)) {
        setSecondToken('');
      }

      setValidSecondTokens(secondTokens);
    }
  }, [firstToken, swapPaths]);

  // useEffect(() => {
  //   console.log(firstTokenAmount.toString(), secondTokenAmount.toString());
  // }, [firstTokenAmount, secondTokenAmount]);

  useEffect(() => {
    if(isFixedoutput) return;

    if(BigZero.gte(firstTokenAmount) || !firstToken || !secondToken) {
      if(BigZero.lt(secondTokenAmount)) {
        setSecondTokenAmount(BigZero);
      }

      return;
    }

    const timer = setTimeout(async () => {
      let bestPath: string[] = [], bestOutAmount = BigZero;

      setSearchingRoutes(true);

      // console.log('find output amount');

      for(const path of validPaths) {
        const outAmount = await viewMultiPathAmountOut(path, firstTokenAmount);
        // console.log(path, outAmount.toString());
        
        if(bestOutAmount.lt(outAmount)) {
          bestOutAmount = outAmount;
          bestPath = path.slice(0);
        }

      }
      
      // const resultAmount = shortenEsdt(bestOutAmount);
      setSecondTokenAmount(new BigNumber(bestOutAmount));
      setOptimalPath(bestPath);
      setSearchingRoutes(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [firstTokenAmount, firstToken, secondToken, validPaths]);

  useEffect(() => {
    if(isFixedInput) return;

    if(BigZero.gte(secondTokenAmount) || !firstToken || !secondToken) {
      if(BigZero.lt(firstTokenAmount)) {
        setFirstTokenAmount(BigZero);
      }

      return;
    }

    const timer = setTimeout(async () => {
      let bestPath: string[] = [], bestInAmount = BigMax;

      setSearchingRoutes(true);

      for(const path of validPaths) {
        const inAmount = await viewMultiPathAmountIn(path, secondTokenAmount);

        // console.log(path, inAmount.toString());

        if(bestInAmount.gt(inAmount)) {
          bestInAmount = inAmount;
          bestPath = path.slice(0);
        }

      }
      
      const resultAmount = bestInAmount.multipliedBy(1 + slippage);

      setFirstTokenAmountWithoutSlippage(new BigNumber(bestInAmount));
      setFirstTokenAmount(new BigNumber(resultAmount));
      setOptimalPath(bestPath);
      setSearchingRoutes(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [secondTokenAmount, firstToken, secondToken]);

  useEffect(() => {
    if(BigZero.lt(firstTokenAmount) && BigZero.lt(firstTokenAmountWithoutSlippage) && isFixedoutput) {
      const newFirstTokenAmount = firstTokenAmountWithoutSlippage.multipliedBy(1 + slippage);

      setFirstTokenAmount(new BigNumber(newFirstTokenAmount));
    }
  }, [slippage]);

  const viewMultiPathAmountOut = async (path: string[], amount_in: BigNumber) => {
    if(path.length < 2) return BigZero;

    // console.log(pairTokenInfos[path[path.length - 1]].decimal);

    try {
      const args: TypedValue[] = [];

      args.push(new BigUIntValue(convertEsdtToWei(amount_in, pairTokenInfos[path[0]]?.decimal)));

      for(const token of path) {
        args.push(BytesValue.fromUTF8(token));
      }

      const amount = await viewMethod({
        contract: contracts.Dex,
        method: 'getMultiPathAmountOut',
        args
      });

      return convertWeiToEsdt(amount, pairTokenInfos[path[path.length - 1]].decimal);
    } catch(error) {
      console.log(error);
      return BigZero;
    }
  };

  const viewMultiPathAmountIn = async (path: string[], amount_out: BigNumber) => {
    if(path.length < 2) return BigZero;

    try {
      const args: TypedValue[] = [];

      args.push(new BigUIntValue(convertEsdtToWei(amount_out, pairTokenInfos[path[path.length - 1]]?.decimal)));

      for(const token of path) {
        args.push(BytesValue.fromUTF8(token));
      }

      const amount = await viewMethod({
        contract: contracts.Dex,
        method: 'getMultiPathAmountIn',
        args
      });

      return convertWeiToEsdt(amount, pairTokenInfos[path[0]].decimal);
    } catch(error) {
      console.log(error);
      return BigZero;
    }
  };

  const swap = async () => {
    if(
      BigZero.gte(firstTokenAmount) || 
      firstTokenAmount.gt(pairTokenInfos[firstToken]?.balance) ||
      BigZero.gte(secondTokenAmount) ||
      !firstToken ||
      !secondToken ||
      optimalPath.length < 2
    ) return;

    // console.log(isFixedInput, isFixedoutput, firstToken, secondToken, wrapRequired, unwrapRequired);
    
    try {
      const args: TypedValue[] = [
        new BooleanValue(unwrapRequired)
      ];

      for(const path of optimalPath) {
        args.push(BytesValue.fromUTF8(path));
      }

      if(isFixedInput) {
        const min_amount = secondTokenAmount.multipliedBy(1 - slippage);

        // console.log(new BigUIntValue(convertEsdtToWei(min_amount, pairTokenInfos[secondToken]?.decimal)).toString());
        
        args.unshift(new BigUIntValue(convertEsdtToWei(min_amount, pairTokenInfos[secondToken]?.decimal)));

        await callMethod({
          contract: contracts.Dex,
          method: 'swapMultiTokensFixedInput',
          args,
          fts: wrapRequired ? []
            : [
              {
                token: firstToken,
                amount: firstTokenAmount,
                decimals: pairTokenInfos[firstToken]?.decimal
              }
            ],
          egld: wrapRequired ? firstTokenAmount : BigZero
          // wrap: wrapRequired ? firstTokenAmount : BigZero,
          // unwrap: unwrapRequired ? min_amount : BigZero,
        });
      } 
      
      else if(isFixedoutput) {
        args.unshift(new BigUIntValue(convertEsdtToWei(secondTokenAmount, pairTokenInfos[secondToken].decimal)));

        await callMethod({
          contract: contracts.Dex,
          method: 'swapMultiTokensFixedOutput',
          args,
          fts: wrapRequired ? []
            : [
              {
                token: firstToken,
                amount: firstTokenAmount,
                decimals: pairTokenInfos[firstToken]?.decimal
              }
            ],
          egld: wrapRequired ? firstTokenAmount : BigZero
          // wrap: wrapRequired ? firstTokenAmount : BigZero,
          // unwrap: unwrapRequired ? secondTokenAmount : BigZero,
        });
      }
      
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className='d-flex flex-column flex-fill align-items-center justify-content-start'>
        <div className={clsx(styles.card, 'card shadow-sm')}>
          <div className={styles.cardBody}>
            <div className='d-flex justify-content-start w-100' style={{ fontSize: 24, color: 'white', paddingLeft: 11.5 }}>
              SWAP
            </div>
            <div style={{ marginTop: 20, width: '100%' }}>
              <PaymentPanel
                title='From'
                isFirstToken={true}
                amount={firstTokenAmount}
                setSwapType={() => {
                  setIsFixedInput(true);
                  setIsFixedOutput(false);
                }}
                setAmount={setFirstTokenAmount}
                tokenId={firstToken}
                onClick={() => setShowFirstTokenModal(true)}
              />
            </div>

            <img onClick={() => {
              if(firstToken && secondToken) {
                const newFirstToken = secondToken;
                const newSecondToken = firstToken;

                setFirstToken(newFirstToken);
                setSecondToken(newSecondToken);
              }
            }} src={UpDown} alt='up down' className={styles.upDown} />
            <PaymentPanel
              title='To'
              isFirstToken={false}
              amount={secondTokenAmount}
              setSwapType={() => {
                setIsFixedInput(false);
                setIsFixedOutput(true);
              }}
              setAmount={setSecondTokenAmount}
              tokenId={secondToken}
              onClick={() => setShowSecondTokenModal(true)}
            />
            <div className={clsx(styles.swapInfo, 'card shadow-sm mt-4')}>
              <div className={styles.swapInfoBody}>
                {
                  searchingRoutes ? (
                    <div className={'d-flex justify-content-between w-100 px-2 mt-2'}>
                      <span>Searching for Routes</span>
                      <Spinner className='text-white' animation="border" />
                    </div>
                  ) : (
                    <>
                      {
                          optimalPath.length ? (
                            <>
                              <div className={'d-flex justify-content-start w-100 px-2 mt-2'}>
                                <span>Optimal Route</span>
                              </div>
                              <div className={'d-flex flex-column align-items-end w-100 px-2 mt-2'}>
                              <span>{optimalPath.map((token) => getSymbol(token)).join(' > ')}</span>
                              </div>
                            </>
                          ) : (
                            BigZero.lt(firstTokenAmount) ? (
                              <span>No swap route found.</span>
                            ) : (
                              <></>
                            )
                          )
                      }
                      {
                        optimalPath.length ? (
                          <>
                            <div className={'d-flex justify-content-start w-100 px-2 mt-2'}>
                              <span>Exchange Rate</span>
                            </div>
                            <div className={'d-flex flex-column align-items-end w-100 px-2 mt-2'}>
                                {
                                  (BigZero.lt(firstTokenAmount) && BigZero.lt(secondTokenAmount)) ? (
                                    <>
                                      <span>1 {getSymbol(firstToken)} ≃ {shortenEsdt(secondTokenAmount.dividedBy(firstTokenAmount), pairTokenInfos[secondToken].decimal)} {getSymbol(secondToken)}</span>
                                      <span>1 {getSymbol(secondToken)} ≃ {shortenEsdt(firstTokenAmount.dividedBy(secondTokenAmount), pairTokenInfos[firstToken].decimal)} {getSymbol(firstToken)}</span>
                                    </>
                                  ) : (
                                    <span>-</span>
                                  )
                                }
                            </div>
                          </>
                        ) : (
                          <></>
                        )
                      }
                    </>
                  )
                }
                <div className={'d-flex justify-content-between w-100 px-2 mt-2'}>
                    <span>Slippage Tolerance</span>
                    <span>{slippage * 100}%</span>
                </div>
                <div className={'d-flex justify-content-end w-100 px-2 mt-2'}>
                  <CustomButton
                    onClick={() => setSlippage(0.0025)}
                    className={clsx(styles.slippageBtn, slippage === 0.0025 ? styles.active : '')}
                  >
                    0.25%
                  </CustomButton>
                  <CustomButton
                    onClick={() => setSlippage(0.01)}
                    className={clsx(styles.slippageBtn, slippage === 0.01 ? styles.active : '')}
                  >
                    1%
                  </CustomButton>
                  <CustomButton
                  onClick={() => setSlippage(0.025)}
                    className={clsx(styles.slippageBtn, slippage === 0.025 ? styles.active : '')}
                  >
                    2.5%
                  </CustomButton>
                  <input className={styles.slippageInput} type='number' placeholder='custom'
                    onChange={(e) => {
                      const value = +e.target.value;

                      if(value > 0 && value <= 100) {
                        setSlippage(value / 100);
                      } else {
                        setSlippage(0.01);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <CustomButton
              className={styles.applyButton}
              onClick={() => {
                if(!userAddress) {
                  navigate(routeNames.unlock);
                }
                swap();
              }}
            >
              { userAddress ? 'SWAP' : 'CONNECT WALLET' }
            </CustomButton>
          </div>
        </div>
      </div>
      <TokenSelector
        tokens={pairTokens}
        setToken={setFirstToken}
        show={showFirstTokenModal}
        setShow={() => setShowFirstTokenModal(false)}
      />
      <TokenSelector
        tokens={validSecondTokens}
        setToken={setSecondToken}
        show={showSecondTokenModal}
        setShow={() => setShowSecondTokenModal(false)}
      />
      {/* <CustomModal
        show={showFirstTokenModal}
        onClose={() => setShowFirstTokenModal(false)}
      /> */}
    </>
  );
};
